import { Product } from 'lib/enums';
import {
  ElasticAggregationBucket,
  ElasticAggregationResult,
  SearchableNewspaperOrderRecord,
  SearchableNoticeRecord
} from 'lib/types/searchable';

export type ReportsV2OrderCustomerTopHitsResult = {
  hits: {
    hits: Array<{
      _source: Pick<
        SearchableNewspaperOrderRecord,
        | 'advertiserorgname'
        | 'filerfirstname'
        | 'filerlastname'
        | 'contactemail'
      >;
    }>;
  };
};

export type ReportsV2NoticeCustomerTopHitsResult = {
  hits: {
    hits: Array<{
      _source: Pick<
        SearchableNoticeRecord,
        'filerorganizationname' | 'filername' | 'fileremail'
      >;
    }>;
  };
};

export type ReportV2CustomerAggregationCohort = {
  cohortKey: string;
  orgName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  orderTypes: {
    [key: string]: {
      revenue: number;
      doc_count: number;
    };
  };
};

export type AdvertiserUserBucket = ElasticAggregationBucket & {
  customerInfo: ReportsV2OrderCustomerTopHitsResult;
  orderType: ElasticAggregationResult;
};

export type AdvertiserOrgBucket = ElasticAggregationBucket & {
  advertiserUser: ElasticAggregationResult;
  customerInfo: ReportsV2OrderCustomerTopHitsResult;
  orderType: ElasticAggregationResult;
};

export type NoticeAdvertiserUserBucket = ElasticAggregationBucket & {
  customerInfo: ReportsV2NoticeCustomerTopHitsResult;
  revenue: { value: number };
  doc_count: number;
};

export type NoticeAdvertiserOrgBucket = ElasticAggregationBucket & {
  advertiserUser: ElasticAggregationResult;
  customerInfo: ReportsV2NoticeCustomerTopHitsResult;
  revenue: { value: number };
};

export const reportsV2ProcessCustomerOrdersBuckets = (
  buckets: AdvertiserOrgBucket[],
  allCohorts: ReportV2CustomerAggregationCohort[]
) => {
  buckets.forEach(orgBucket => {
    const orgCustomerInfo: ReportsV2OrderCustomerTopHitsResult =
      orgBucket.customerInfo;
    const topHit = orgCustomerInfo?.hits?.hits[0]?._source;
    const orgName = topHit?.advertiserorgname;

    const advertiserUserBuckets = orgBucket.advertiserUser
      ?.buckets as AdvertiserUserBucket[];

    if (orgBucket.key === 'none') {
      // For "none" org bucket, treat each user as individual
      advertiserUserBuckets.forEach(userBucket => {
        const userCustomerInfo: ReportsV2OrderCustomerTopHitsResult =
          userBucket.customerInfo;
        // use the top hit as representative
        const topHit = userCustomerInfo?.hits?.hits[0]?._source;
        const firstName = topHit?.filerfirstname;
        const lastName = topHit?.filerlastname;
        const email = topHit?.contactemail;

        const orderTypes: ReportV2CustomerAggregationCohort['orderTypes'] = {};
        const orderTypeBuckets = userBucket.orderType
          ?.buckets as ElasticAggregationBucket[];
        if (orderTypeBuckets) {
          orderTypeBuckets.forEach(orderTypeBucket => {
            const orderType = orderTypeBucket.key;
            orderTypes[orderType] = {
              revenue:
                (orderTypeBucket.revenue as { value: number })?.value || 0,
              doc_count: orderTypeBucket.doc_count
            };
          });
        }

        allCohorts.push({
          cohortKey: userBucket.key as string,
          firstName,
          lastName,
          email,
          orderTypes
        });
      });
    } else {
      // For regular org buckets, group all users under the org
      const orderTypes: ReportV2CustomerAggregationCohort['orderTypes'] = {};
      let orgEmail: string | undefined;

      advertiserUserBuckets.forEach(userBucket => {
        const userCustomerInfo: ReportsV2OrderCustomerTopHitsResult =
          userBucket.customerInfo;
        // use the top hit as representative
        const topHit = userCustomerInfo.hits?.hits[0]?._source;
        if (!orgEmail && topHit?.contactemail) {
          orgEmail = topHit.contactemail;
        }

        const orderTypeBuckets = userBucket.orderType
          ?.buckets as ElasticAggregationBucket[];
        if (orderTypeBuckets) {
          orderTypeBuckets.forEach(orderTypeBucket => {
            const orderType = orderTypeBucket.key;
            const revenue =
              (orderTypeBucket.revenue as { value: number })?.value || 0;
            const docCount = orderTypeBucket.doc_count;

            if (!orderTypes[orderType]) {
              orderTypes[orderType] = { revenue: 0, doc_count: 0 };
            }
            orderTypes[orderType].revenue += revenue;
            orderTypes[orderType].doc_count += docCount;
          });
        }
      });

      allCohorts.push({
        cohortKey: orgBucket.key as string,
        orgName,
        email: orgEmail,
        orderTypes
      });
    }
  });
  return allCohorts;
};

export const reportsV2ProcessCustomerNoticesBuckets = (
  buckets: NoticeAdvertiserOrgBucket[],
  allCohorts: ReportV2CustomerAggregationCohort[]
) => {
  buckets.forEach(orgBucket => {
    const orgCustomerInfo = orgBucket.customerInfo?.hits?.hits[0]?._source;
    const orgName = orgCustomerInfo?.filerorganizationname;
    const advertiserUserBuckets = orgBucket.advertiserUser
      ?.buckets as NoticeAdvertiserUserBucket[];

    if (orgBucket.key === 'none') {
      // For "none" org bucket, treat each user as individual
      advertiserUserBuckets.forEach(userBucket => {
        const userCustomerInfo: ReportsV2NoticeCustomerTopHitsResult =
          userBucket.customerInfo;
        const firstHit = userCustomerInfo?.hits?.hits[0]?._source;
        const firstName = firstHit?.filername?.split(' ')[0];
        const lastName = firstHit?.filername?.split(' ').slice(1).join(' ');
        const email = firstHit?.fileremail;
        const noticeRevenue = userBucket.revenue?.value || 0;

        let existingCohort = allCohorts.find(
          cohort => cohort.cohortKey === userBucket.key
        );

        if (!existingCohort) {
          existingCohort = {
            cohortKey: userBucket.key as string,
            firstName,
            lastName,
            email,
            orderTypes: {}
          };
          allCohorts.push(existingCohort);
        } else if (!existingCohort.email && email) {
          existingCohort.email = email;
        }

        if (!existingCohort.orderTypes[Product.Notice]) {
          existingCohort.orderTypes[Product.Notice] = {
            revenue: 0,
            doc_count: 0
          };
        }
        existingCohort.orderTypes[Product.Notice].revenue += noticeRevenue;
        existingCohort.orderTypes[Product.Notice].doc_count +=
          userBucket.doc_count || 0;
      });
    } else {
      // For regular org buckets, group all users under the org
      let existingCohort = allCohorts.find(
        cohort => cohort.cohortKey === orgBucket.key
      );

      if (!existingCohort) {
        existingCohort = {
          cohortKey: orgBucket.key as string,
          orgName,
          email: undefined,
          orderTypes: {}
        };
        allCohorts.push(existingCohort);
      }

      let orgEmail: string | undefined;
      let totalNoticeRevenue = 0;
      let totalNoticeCount = 0;

      advertiserUserBuckets.forEach(userBucket => {
        const userCustomerInfo: ReportsV2NoticeCustomerTopHitsResult =
          userBucket.customerInfo;

        const firstHit = userCustomerInfo?.hits?.hits[0]?._source;
        if (!orgEmail && firstHit?.fileremail) {
          orgEmail = firstHit.fileremail;
        }

        totalNoticeRevenue += userBucket.revenue?.value || 0;
        totalNoticeCount += userBucket.doc_count || 0;
      });

      if (!existingCohort!.orderTypes[Product.Notice]) {
        existingCohort!.orderTypes[Product.Notice] = {
          revenue: 0,
          doc_count: 0
        };
      }
      existingCohort!.orderTypes[Product.Notice].revenue += totalNoticeRevenue;
      existingCohort!.orderTypes[Product.Notice].doc_count += totalNoticeCount;

      if (!existingCohort!.email && orgEmail) {
        existingCohort!.email = orgEmail;
      }
    }
  });
  return allCohorts;
};
